package ui.device.detail.preview

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.compose.navigation.navTo_DeviceEditorUI
import tk.mallumo.utils.DataState
import tools.rememberString
import ui.device.detail.DeviceArgs
import ui.device.detail.editor.DeviceEditorArgs
import ui.device.detail.preview.screen.DeviceScreen01_Info
import ui.device.detail.preview.screen.DeviceScreen02_Playlist
import ui.device.detail.preview.screen.DeviceScreen03_Apps
import ui.device.detail.preview.screen.DeviceScreen04_Commands
import ui.device.detail.preview.screen.DeviceScreen05_Monitoring
import ui.widget.*

@ComposableNavNode(DeviceArgs::class)
@Composable
fun DevicePreviewUI() {
    with(DevicePreviewScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun DevicePreviewScope.Content(nnvl: DevicePreviewScope.Wrapper) {
    val deleteState = DialogState.remember()

    DeleteDialog(deleteState)
    val toolbarActions = remember {
        buildList {
            if (nnvl.canDelete) {
                add(ToolbarAction.delete { deleteState.show() })
            }
            if (nnvl.canEdit) {
                add(ToolbarAction.edit {
                    nav.navTo_DeviceEditorUI(
                        DeviceEditorArgs(
                            deviceId = args.deviceId
                        )
                    )
                })
            }
        }
    }
    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = Res.string.device_detail__title.rememberString(),
            titleSuffix = "(${nnvl.data.id})",
            actions = toolbarActions
        )
        Crossfade(vm.selectedTab.value, Modifier.weight(1F).fillMaxWidth()) {
            when (it.tag) {
                DevicePreviewVM.TabType.INFO -> DeviceScreen01_Info(nnvl)
                DevicePreviewVM.TabType.LAYER_PRESENTATION -> DeviceScreen02_Playlist(nnvl)
                DevicePreviewVM.TabType.LAYER_APPLICATION -> DeviceScreen03_Apps(nnvl)
                DevicePreviewVM.TabType.ACTION -> DeviceScreen04_Commands(nnvl)
                DevicePreviewVM.TabType.REPORT_SYSTEM -> DeviceScreen05_Monitoring(nnvl)
            }
        }
        if (vm.tabs.size > 1) {
            if (display.isMobileOrSmall) {
                ScrollableTabRow(
                    selectedTabIndex = vm.tabs.indexOfFirst { it.tag == vm.selectedTab.value.tag },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    vm.tabs.forEach { tabDefinition ->
                        Tab(
                            selected = tabDefinition.tag == vm.selectedTab.value.tag,
                            onClick = { tabDefinition.onClick(tabDefinition) },
                            text = { Text(tabDefinition.label.rememberString()) },
                            icon = { Icon(tabDefinition.icon, null) })
                    }
                }
            } else {
                NavigationBar(Modifier.fillMaxWidth()) {
                    vm.tabs.forEach { tabDefinition ->
                        NavigationBarItem(
                            selected = tabDefinition.tag == vm.selectedTab.value.tag,
                            onClick = { tabDefinition.onClick(tabDefinition) },
                            label = {
                                Text(
                                    text = tabDefinition.label.rememberString(),
                                    textAlign = TextAlign.Center
                                )
                            },
                            icon = { Icon(tabDefinition.icon, null) })
                    }
                }
            }
        }
    }
}

@Composable
fun DevicePreviewScope.DeleteDialog(deleteState: DialogState) {
    Dialog(deleteState) {
        DialogUI(
            Modifier.align(Alignment.Center),
            onDismissRequest = { deleteState.hide() },
            title = { Text(Res.string.device_detail__delete_title.rememberString()) },
            confirmButton = {
                TextButton({
                    action(DevicePreviewVM.Action.Delete)
                    deleteState.hide()
                }) { Text(Res.string.dialog__action_delete.rememberString()) }
            },
            dismissButton = { TextButton({ deleteState.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            Text(Res.string.device_detail__delete_message.rememberString())
        }
    }
}

